import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '~/core/services/auth.service';

/**
 * HTTP Interceptor for JWT authentication
 * Adds Authorization header from MSAL token and extracts roles for RoleService
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const env = (window as unknown as { __env__?: Record<string, string | undefined> }).__env__;
  const apiUrl = (env?.['apiUrl'] ?? env?.['COOASS_API_URL'] ?? '').trim();
  const isBackendRequest =
    req.url.startsWith('/api/v1/') ||
    req.url.includes('localhost') ||
    (!!apiUrl && req.url.startsWith(apiUrl));

  // Catch only token acquisition failures; do not swallow API response errors.
  const token$ = from(authService.getToken()).pipe(
    catchError((error) => {
      if (isBackendRequest) {
        console.warn('[AUTH] Interceptor failed to acquire token for backend request', {
          method: req.method,
          url: req.url,
          error,
        });
      }
      return from(Promise.resolve<string | null>(null));
    })
  );

  return token$.pipe(
    switchMap((token) => {
      if (!token) {
        if (isBackendRequest) {
          console.warn('[AUTH] Sending backend request without Authorization header', {
            method: req.method,
            url: req.url,
          });
        }
        return next(req);
      }

      const authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (isBackendRequest) {
        console.info('[AUTH] Authorization header attached to backend request', {
          method: req.method,
          url: req.url,
        });
      }

      return next(authReq);
    })
  );
};
